import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Grid,
  Input,
  Select,
  Text,
  Tooltip,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'src/utils/auth';
import { apiRequest } from 'src/utils/fetchUtils';
import { isUrlValid } from 'src/utils/helper';
import { useRouter } from 'next/router';
import { useMarketplaceContext } from 'src/contexts/marketplace';
import UpsellTierModal from 'src/components/atoms/upsell-tier-modal';
import { FEATURE_IDS, FeatureId } from 'src/constants/tier-features';

export enum ListingImportOption {
  ebay = 'ebay', // Example URL: https://www.ebay.com/itm/134638042371?var=434175605789
  craigslist = 'craigslist', // Example URL: https://sfbay.craigslist.org/eby/tro/d/port-costa-truck-camper/7657688515.html
  other = 'other',
}

const EBAY_REGEX = /^https:\/\/www.ebay.com\/itm\/.+/;
const CRAIGSLIST_REGEX = /https:\/\/.+.craigslist.org\/.+.html$/;
const OTHER_REGEX = /^https:\/\/.+/;

function checkSourceURL(url: string) {
  if (CRAIGSLIST_REGEX.test(url)) return ListingImportOption.craigslist;
  if (EBAY_REGEX.test(url)) return ListingImportOption.ebay;
  if (OTHER_REGEX.test(url)) return ListingImportOption.other;
}

interface ListingImportProps {
  categoryId: number | undefined;
}

const ListingImport: React.FC<ListingImportProps> = ({ categoryId }) => {
  const { getToken, hasAdmin, hasRole } = useAuth();
  const { marketplace } = useMarketplaceContext();
  const isAdmin = hasAdmin(marketplace?.id);
  const isLomaAdmin = hasRole(marketplace?.id?.toString() ?? '', 'loma_admin');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const router = useRouter();
  const btnRef = useRef<any>();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [upgradeFeature, setUpgradeFeature] = useState<FeatureId | null>(null);

  const [state, setState] = useState<{
    url: any;
    type: ListingImportOption | string;
    categoryId: number | undefined;
  }>({
    url: null,
    type: '',
    categoryId: categoryId,
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<any>({ status: null, content: null });
  const [importId, setImportId] = useState<string | null>(null);
  const [polling, setPolling] = useState(false);
  const [pollError, setPollError] = useState<string | null>(null);
  const [importStatus, setImportStatus] = useState<'idle' | 'running' | 'completed' | 'failed'>(
    'idle',
  );
  const [createdListingId, setCreatedListingId] = useState<number | null>(null);
  const pollRef = useRef<any>(null);

  const handleImport = useCallback(async () => {
    setMessage({ status: null, content: null });
    const source = checkSourceURL(state.url);
    if (!source) {
      setMessage({ status: 'error', content: 'Invalid URL' });
      return;
    }
    setLoading(true);

    try {
      const token = await getToken();
      const url = `${process.env.NEXT_PUBLIC_API_HOST}/listings/import`;
      const response = await apiRequest('POST', url, token, state);
      if (source === ListingImportOption.other) {
        const newImportId = response?.import_id as string | undefined;
        if (!newImportId) {
          throw new Error('Unable to start import. Please try again.');
        }
        setImportId(newImportId);
        setImportStatus('running');
        setPolling(true);
        setPollError(null);
        return; // stay on drawer and start polling
      }
      const listingId = response.id;
      if (listingId) {
        router.push(
          `/listing/edit/${listingId}?success=true&message=You+can+now+edit+and+publish+your+listing.`,
        );
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Unable to import listing. Please check the URL and try again.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'bottom',
      });
    } finally {
      setLoading(false);
    }
  }, [getToken, state]);

  const fetchImportStatus = useCallback(async () => {
    if (!importId) return;
    if (!marketplace?.id) return;
    try {
      const token = await getToken();
      const url = `${process.env.NEXT_PUBLIC_API_HOST}/marketplaces/${marketplace?.id}/listings/import/status/${importId}/`;
      const res = await apiRequest('GET', url, token);
      const results: any[] = res?.results || [];

      // Determine status
      let status: 'running' | 'completed' | 'failed' = 'running';
      let listingId: number | null = null;
      let errorMessage: string | null = null;

      for (const r of results) {
        if (typeof r?.listing_id === 'number') {
          listingId = r.listing_id;
        }
        if (r?.status === 'completed') {
          status = 'completed';
        }
        if (r?.error) {
          status = 'failed';
          // Map to user-readable messages
          const err = String(r.error);
          if (err === 'invalid_url') errorMessage = 'The URL provided is invalid.';
          else if (err === 'invalid_category') errorMessage = 'Selected category was not found.';
          else if (err === 'invalid_user') errorMessage = 'User is not authorized to import.';
          else if (err === 'scrape_failed')
            errorMessage = 'We could not read details from that URL.';
          else errorMessage = 'An unexpected error occurred while importing.';
        }
      }

      setCreatedListingId(listingId);
      if (status === 'completed') {
        setImportStatus('completed');
        setPolling(false);
        setPollError(null);
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } else if (status === 'failed') {
        setImportStatus('failed');
        setPolling(false);
        setPollError(errorMessage || 'Import failed.');
        if (pollRef.current) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } else {
        setImportStatus('running');
      }
    } catch (e) {
      setPollError('Unable to fetch import status.');
    }
  }, [getToken, importId, marketplace?.id]);

  useEffect(() => {
    if (polling && importId && !pollRef.current && marketplace?.id) {
      // immediately fetch then poll
      fetchImportStatus();
      pollRef.current = setInterval(() => {
        fetchImportStatus();
      }, 4000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [polling, importId, fetchImportStatus, marketplace?.id]);

  const isDisabled = !(isUrlValid(state.url) && state.type);

  const isSingleListingImportAvailable =
    marketplace?.tier?.features?.[FEATURE_IDS.SINGLE_LISTING_IMPORT] !== false;

  const onUpgradeModalClose = () => {
    setIsUpgradeModalOpen(false);
    setUpgradeFeature(null);
  };

  const handleLockedFeatureClick = (featureID: FeatureId) => {
    setUpgradeFeature(featureID);
    setIsUpgradeModalOpen(true);
  };

  const handleImportButtonClick = () => {
    if (!isSingleListingImportAvailable) {
      handleLockedFeatureClick(FEATURE_IDS.SINGLE_LISTING_IMPORT);
      return;
    }

    setState({ url: null, type: '', categoryId: categoryId });
    onOpen();
  };

  if (!isAdmin && !isLomaAdmin) return null;

  return (
    <>
      {!isSingleListingImportAvailable ? (
        <Button ref={btnRef} onClick={handleImportButtonClick}>
          Import
        </Button>
      ) : categoryId ? (
        <Button ref={btnRef} onClick={handleImportButtonClick}>
          Import
        </Button>
      ) : (
        <Tooltip label="Please select a category before importing">
          <span>
            <Button isDisabled>Import</Button>
          </span>
        </Tooltip>
      )}
      <Drawer
        size={'sm'}
        isOpen={isOpen}
        placement="right"
        onClose={() => {
          if (loading) return;
          onClose();
        }}
        finalFocusRef={btnRef}
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Import Listing</DrawerHeader>
          <DrawerBody>
            <Grid gap={30}>
              <div>
                <Text>URL:</Text>
                <Input
                  disabled={loading || importStatus === 'running'}
                  isInvalid={state.url && !isUrlValid(state.url)}
                  placeholder="URL"
                  value={_.get(state, 'url', '')}
                  onChange={(e) => setState((prev) => ({ ...prev, url: e.target.value }))}
                />
              </div>
              <div>
                <Text>Select Source</Text>
                <Select
                  disabled={loading || importStatus === 'running'}
                  placeholder="Source"
                  value={_.get(state, 'type', '')}
                  onChange={(e) => setState((prev) => ({ ...prev, type: e.target.value }))}
                >
                  <option value={ListingImportOption.craigslist}>
                    Craigslist (Individual Listing URL)
                  </option>
                  <option value={ListingImportOption.ebay}>eBay (Individual Item URL)</option>
                  {(isAdmin || isLomaAdmin) && (
                    <option value={ListingImportOption.other}>
                      Other (Individual Listing URL)
                    </option>
                  )}
                </Select>
              </div>
            </Grid>
            {importId && (
              <Box mt={6}>
                {importStatus === 'running' && (
                  <Alert status="info">
                    <AlertIcon /> We are importing your listing. This may take a minute...
                  </Alert>
                )}
                {importStatus === 'completed' && (
                  <Alert status="success">
                    <AlertIcon /> Listing imported successfully.
                  </Alert>
                )}
                {importStatus === 'failed' && (
                  <Alert status="error">
                    <AlertIcon /> {pollError || 'Import failed.'}
                  </Alert>
                )}
              </Box>
            )}
            {message.status && (
              <Alert status={message.status} mt={3}>
                <AlertIcon />
                {message.content}
              </Alert>
            )}
          </DrawerBody>
          <DrawerFooter>
            <Button
              isDisabled={loading || importStatus === 'running'}
              variant="outline"
              mr={3}
              onClick={() => {
                if (pollRef.current) {
                  clearInterval(pollRef.current);
                  pollRef.current = null;
                }
                // reset state when closing
                setImportId(null);
                setPolling(false);
                setPollError(null);
                setImportStatus('idle');
                setCreatedListingId(null);
                onClose();
              }}
            >
              Cancel
            </Button>
            {importStatus === 'completed' && createdListingId ? (
              <Button
                colorScheme="green"
                onClick={() =>
                  router.push(
                    `/listing/edit/${createdListingId}?success=true&message=You+can+now+edit+and+publish+your+listing.`,
                  )
                }
              >
                Open Listing
              </Button>
            ) : importStatus === 'completed' ? (
              <Button
                colorScheme="green"
                onClick={() =>
                  router.push('/admin/approvals?success=true&message=Listing+imported')
                }
              >
                View in Approvals
              </Button>
            ) : (
              <Button
                onClick={handleImport}
                colorScheme="blue"
                isLoading={loading}
                isDisabled={isDisabled || (!!importId && importStatus === 'running')}
              >
                Import
              </Button>
            )}
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {upgradeFeature && (
        <UpsellTierModal
          isOpen={isUpgradeModalOpen}
          onClose={onUpgradeModalClose}
          featureId={upgradeFeature}
        />
      )}
    </>
  );
};

export default ListingImport;
